import React, { useState } from 'react';
import { 
  FileSignature, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle,
  ShieldCheck, Globe
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import { LocalModelToggle } from './LocalModelToggle';

interface LegalDocCreatorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація модуля конструктора документів...",
  "Юрисконсульт-аналітик розбирає вимоги та нормативи (Kodeks cywilny / ПКУ / ДСТУ)...",
  "Аналітик судової практики перевіряє правові застереження та арбітражні норми...",
  "Старший юридичний редактор формує статті, обов'язки, санкції та реквізити...",
  "Генерація фінального юридичного документа з підтримкою ДСТУ та Word (.docx)..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja modułu kreatora dokumentów prawnych...",
  "Analityk prawny weryfikuje przepisy (Kodeks cywilny, Kodeks pracy, KSH, KSeF)...",
  "Ekspert orzecznictwa sprawdza klauzule zabezpieczające i właściwość sądową...",
  "Starszy redaktor prawny dopracowuje artykuły, sankcje umowne i dane stron...",
  "Generowanie ostatecznego dokumentu prawnego z eksportem do Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing legal document creator AI agents...",
  "Legal analyst parses statutory requirements and jurisdiction norms (Civil Code / Labor Code)...",
  "Case law analyst checks liability clauses, arbitration, and penalty terms...",
  "Senior editor refines formatting, bilingual clauses, and corporate metadata...",
  "Generating final legal document with native Word (.docx) export..."
];

export default function LegalDocCreator({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: LegalDocCreatorProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  const [documentType, setDocumentType] = useState(
    isPl ? 'Umowa o świadczenie usług B2B (Kodeks cywilny / KSeF)' : isUk ? 'Позовна заява' : 'Lawsuit Statement'
  );
  const [caseFactsText, setCaseFactsText] = useState(
    useEnterpriseContext 
      ? (isPl ? 'Dane rejestracyjne i profil przedsiębiorstwa zostaną automatycznie pobrane z bazy wiedzy.' : isUk ? 'Дані нашого підприємства (реквізити, назва, КВЕДи) будуть автоматично підтягнуті з бази знань кабінету.' : 'Our company registration details and profile will be automatically loaded from the workspace knowledge base.') 
      : ''
  );
  const [specificClausesText, setSpecificClausesText] = useState('');
  
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [showSignModal, setShowSignModal] = useState(false);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  // Cycle loading steps
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk, isPl]);

  const handleRun = async () => {
    if (!caseFactsText.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Конструктор та помічник у складанні юридичних документів",
          agentId: "legal-doc-creator",
          useLocalModel,
          inputs: {
            document_type: documentType,
            case_facts_text: caseFactsText,
            specific_clauses_text: specificClausesText || (isPl ? "Brak dodatkowych zastrzeżeń (standardowe klauzule ustawowe)" : isUk ? "Немає особливих побажань (стандартні норми)" : "Standard statutory clauses"),
            target_language: isPl ? 'pl' : isUk ? 'uk' : 'en'
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd generatora dokumentów.' : isUk ? 'Помилка конструктора документів.' : 'Failed to generate document.'));
      }

      const data = await response.json();
      
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isPl ? 'Błąd sprawdzania statusu zadania.' : isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isPl ? 'Serwer zwrócił pusty dokument.' : isUk ? 'Сервер повернув порожній результат документа.' : 'Server returned empty document.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isPl ? 'Błąd generowania dokumentu.' : isUk ? 'Помилка генерації документа.' : 'Document generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2500);
            }
          } catch (pollErr: any) {
            console.error('Polling error:', pollErr);
            setError(pollErr.message || (isPl ? 'Wystąpił błąd podczas pobierania dokumentu.' : isUk ? 'Сталася помилка при отриманні результатів.' : 'Polling error occurred.'));
            setIsAuditing(false);
          }
        };
        pollJob();
      } else if (data.report) {
        setReport(data.report);
        setIsAuditing(false);
      } else {
        throw new Error(isPl ? 'Nieoczekiwana odpowiedź serwera.' : isUk ? 'Неочікувана відповідь сервера.' : 'Unexpected server response.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isPl ? 'Wystąpił błąd podczas generowania dokumentu.' : isUk ? 'Сталася помилка під час складання документа.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = documentType.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_") + "_draft";
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isPl ? 'Nie udało się wygenerować pliku Word.' : isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isPl ? "Strona Główna" : isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isPl ? "← Powrót do Centrum" : isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <FileSignature className="w-6 h-6 text-purple-600" />
              <span>{isPl ? "Kreator Umów i Dokumentów Prawnych (Polska / UA / UE)" : isUk ? "Конструктор юридичних документів & Договорів (Україна / Польща / ЄС)" : "AI Legal Document & Contract Creator"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isPl
                ? "Generowanie umów B2B, umów o pracę/zlecenie, protokołów robót i uchwał zgodnie z prawem polskim (Kodeks cywilny, Kodeks pracy, KSH, KSeF)"
                : isUk 
                ? "Складання позовів, претензій, B2B контрактів, польських угод (Kodeks cywilny, KSeF) та кадрових договорів" 
                : "Generates lawsuits, claims, B2B contracts, Polish labor/service agreements, and acts compliant with statutory laws"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
          <div className="bg-blue-500/10 border border-blue-500/30 px-3 py-1 rounded-full flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">PL • UA • EU JURISDICTIONS</span>
          </div>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isPl ? "Aktywny kontekst firmy: " : isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isPl ? "Baza wiedzy przedsiębiorstwa" : isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isPl ? "Corporate DNA" : isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          {/* Fast Preset Templates Header */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="text-xs font-extrabold uppercase tracking-wide flex items-center gap-1.5 text-amber-400">
                <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
                <span>{isPl ? "⚡ Gotowe szablony dokumentów (1 kliknięcie do wypełnienia):" : isUk ? "⚡ Готові шаблони документів Польщі & України (1 клік):" : "⚡ Ready Document Templates (1-Click Fill):"}</span>
              </span>
              <span className="text-[10px] text-slate-400 font-mono">Kodeks cywilny • Kodeks pracy • KSH • KSeF • ДСТУ</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {/* Preset 1: Poland B2B */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Umowa o świadczenie usług B2B (Kodeks cywilny / KSeF)");
                  setCaseFactsText(`Umowa o świadczenie usług programistycznych, doradczych i wdrożeniowych B2B.
Zleceniodawca (Замовник): 'Tech Logistics Sp. z o.o.' (NIP: PL5252812345, KRS: 0000843210, REGON: 389102450, ul. Marszałkowska 126, 00-008 Warszawa, Polska).
Wykonawca (Виконавець): ТОВ 'Софт-Інжиніринг' (ЄДРПОУ: 38812390, NIP PL: PL9999999999, Kijów, Ukraina).
Przedmiot: Świadczenie usług utrzymania systemów IT, optymalizacji baz danych i audytu architektury chmurowej (SLA 99.9%).
Wynagrodzenie: 4 500 EUR netto / miesiąc (lub równowartość w PLN). Płatność w terminie 14 dni od wystawienia faktury VAT w systemie KSeF.
Mechanizm: Reverse Charge (odwrotne obciążenie VAT, art. 28b ustawy o VAT / art. 196 Dyrektywy 2006/112/WE).`);
                  setSpecificClausesText("Klauzula poufności (NDA) z karą umowną 25 000 EUR, zakaz konkurencji w trakcie trwania umowy, właściwość sądu: Sąd Arbitrażowy przy Krajowej Izbie Gospodarczej w Warszawie (KIG), język kontraktu: polski i ukraiński (wersja dwujęzyczna).");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-blue-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-blue-300 group-hover:text-blue-200 flex items-center gap-1.5">
                  <span>🇵🇱 Umowa B2B (KSeF / PL-UA)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Kodeks cywilny • Reverse Charge VAT</div>
              </button>

              {/* Preset 2: Umowa Zlecenie */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Umowa zlecenie (Kodeks cywilny / ZUS)");
                  setCaseFactsText(`Umowa zlecenie na wykonanie prac specjalistycznych / doradczych.
Zleceniodawca (Pracodawca/Firma): 'Nova Retail Sp. z o.o.' (NIP: PL5213948210, REGON: 142859102, ul. Złota 59, 00-120 Warszawa).
Zleceniobiorca (Wykonawca): Jan Kowalski / Іван Ковальчук (PESEL: 89041209384, Paszport/Dowód: AB123456, zam. ul. Prosta 10, Warszawa).
Przedmiot zlecenia: Obsługa logistyczna, koordynacja przesyłek magazynowych oraz weryfikacja dokumentów CMR.
Stawka godzinowa: 30,50 PLN brutto za godzinę (nie mniej niż obowiązująca ustawowa minimalna stawka godzinowa).
Miesięczny wymiar: orientacyjnie 120-160 godzin w miesiącu, rozliczany na podstawie comiesięcznej ewidencji godzin.
Zgłoszenie do ZUS: Obowiązkowe ubezpieczenie emerytalne, rentowe, wypadkowe oraz zdrowotne (kod tytułu 04 11).`);
                  setSpecificClausesText("Comiesięczne potwierdzenie liczby godzin (Ewidencja czasu pracy), wypłata do 10. dnia kolejnego miesiąca na rachunek bankowy, możliwość wypowiedzenia z zachowaniem 7-dniowego okresu uprzedzenia.");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1.5">
                  <span>🇵🇱 Umowa Zlecenie (ZUS / Kodeks cywilny)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Stawka godzinowa • Ewidencja godzin • ZUS</div>
              </button>

              {/* Preset 3: Umowa o pracę */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Umowa o pracę (Kodeks pracy / Polska)");
                  setCaseFactsText(`Umowa o pracę na czas określony (12 miesięcy) / na okres próbny (3 miesiące).
Pracodawca: 'Bud-Projekt Sp. z o.o.' (NIP: PL5842791100, KRS: 0000712390, ul. Grunwaldzka 18, 80-236 Gdańsk).
Pracownik: Anna Nowak (PESEL: 94061803921, zam. ul. Długa 5, Gdańsk).
Stanowisko: Inżynier Budowy / Specjalista ds. Kosztorysowania (pełny etat - 40 godzin tygodniowo).
Miejsce wykonywania pracy: Siedziba pracodawcy oraz obiekty budowlane na terenie województwa pomorskiego.
Wynagrodzenie zasadnicze: 7 500,00 PLN brutto miesięcznie + premia regulaminowa do 15% uzależniona od wyników projektu.
Termin rozpoczęcia pracy: 1 września 2026 r.`);
                  setSpecificClausesText("Normy czasu pracy (8h/dobę, 40h/tydzień), prawo do corocznego płatnego urlopu wypoczynkowego (26 dni), okres wypowiedzenia zgodnie z art. 36 Kodeksu pracy, obowiązek zachowania tajemnicy przedsiębiorstwa.");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-teal-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-teal-300 group-hover:text-teal-200 flex items-center gap-1.5">
                  <span>🇵🇱 Umowa o Pracę (Kodeks pracy)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Etat • Urlop • ZUS • Kodeks pracy</div>
              </button>

              {/* Preset 4: Protokół Odbioru Robót */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Protokół odbioru robót budowlanych (Prawo budowlane)");
                  setCaseFactsText(`Protokół końcowego odbioru robót budowlanych i montażowych.
Inwestor / Zamawiający: 'Centrum Logistyczne Mazovia Sp. z o.o.' (NIP: PL7010982341, ul. Przemysłowa 4, Pruszków).
Generalny Wykonawca: 'Bud-Inwestycje Polska Sp. z o.o.' (NIP: PL5272891102, Al. Jerozolimskie 100, Warszawa).
Obiekt / Inwestycja: Budowa hali magazynowo-produkcyjnej z zapleczem socjalno-biurowym (Pozwolenie na budowę nr 142/2025).
Wartość zrealizowanych robót objętych odbiorem: 1 850 000,00 PLN netto (2 275 500,00 PLN brutto).
Data rozpoczęcia robót: 10 stycznia 2026 r., Data zakończenia robót: 30 lipca 2026 r.
Komisja odbiorowa w składzie:
1. Inspektor Nadzoru Inwestorskiego: inż. Marek Wiśniewski (uprawnienia budowlane MAZ/0124/OWOK/15).
2. Kierownik Budowy: inż. Tomasz Lewandowski (uprawnienia budowlane MAZ/0981/WB/12).
3. Przedstawiciel Zamawiającego: Piotr Kamiński - Członek Zarządu.`);
                  setSpecificClausesText("Jakość robót oceniono jako dobrą, roboty wykonano zgodnie z projektem budowlanym i Polskimi Normami PN-EN. Wykaz drobnych usterek bezwpływowych z terminem usunięcia 14 dni. Okres gwarancji jakości: 60 miesięcy, rękojmia: 60 miesięcy od daty podpisania protokołu. Podstawa do wystawienia końcowej faktury VAT KSeF.");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-amber-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-amber-300 group-hover:text-amber-200 flex items-center gap-1.5">
                  <span>🇵🇱 Protokół Odbioru Robót (Budownictwo)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Prawo budowlane • Kierownik budowy • KSeF</div>
              </button>

              {/* Preset 5: Najem Lokalu */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Umowa najmu lokalu użytkowego (Polska / GUS)");
                  setCaseFactsText(`Umowa najmu lokalu użytkowego na cele biurowo-usługowe.
Wynajmujący: 'Mazowiecka Nieruchomości Sp. z o.o.' (NIP: PL5251029384, KRS: 0000391024, Warszawa).
Najemca: 'Global Tech Consulting Sp. z o.o.' (NIP: PL5213948190, KRS: 0000812903, Warszawa).
Przedmiot najmu: Samodzielny lokal użytkowy nr 304 o powierzchni 185,5 m² w budynku biurowym 'Tower Plaza' przy ul. Emilii Plater 53 w Warszawie.
Czynsz najmu: 18,50 EUR netto / m² miesięcznie (łącznie 3 431,75 EUR netto / miesiąc) + opłata eksploatacyjna (service charge) 24,00 PLN netto / m².
Okres najmu: Czas oznaczony 3 lata (36 miesięcy).
Kaucja gwarancyjna: Równowartość 3-miesięcznego czynszu wraz z opłatami eksploatacyjnymi (płatna w terminie 7 dni od podpisania umowy).`);
                  setSpecificClausesText("Coroczna waloryzacja czynszu według wskaźnika HICP Eurostatu / wskaźnika GUS, protokół zdawczo-odbiorczy stanu lokalu, zakaz podnajmu bez uprzedniej pisemnej zgody Wynajmującego, poddanie się egzekucji w trybie art. 777 KPC.");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-purple-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-purple-300 group-hover:text-purple-200 flex items-center gap-1.5">
                  <span>🇵🇱 Najem Lokalu Użytkowego (GUS / Kaucja)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Kodeks cywilny • Kaucja • Waloryzacja GUS</div>
              </button>

              {/* Preset 6: Uchwała Sp. z o.o. */}
              <button
                type="button"
                onClick={() => {
                  setDocumentType("Uchwała Zarządu / Wspólników Sp. z o.o. (KSH)");
                  setCaseFactsText(`Uchwała nr 4/2026 Nadzwyczajnego Zgromadzenia Wspólników Spółki pod firmą 'Baltic Logistics Sp. z o.o.' z siedzibą w Warszawie (KRS 0000912384, NIP 5252891024, kapitał zakładowy: 100 000,00 PLN).
Miejsce i data: Warszawa, ul. Prosta 20, 14 sierpnia 2026 roku.
Obecni Wspólnicy reprezentujący 100% udziałów w kapitale zakładowym:
1. Oleksandr Petrenko - posiadający 60 udziałów o łącznej wartości nominalnej 60 000 PLN.
2. Michał Kowalczyk - posiadający 40 udziałów o łącznej wartości nominalnej 40 000 PLN.
Przedmiot uchwały:
1. Wyrażenie zgody przez Zgromadzenie Wspólników na zaciągnięcie zobowiązania i zawarcie umowy leasingu operacyjnego 5 ciągników siodłowych o wartości przekraczającej dwukrotność kapitału zakładowego (art. 230 Kodeksu spółek handlowych).
2. Upoważnienie Prezesa Zarządu do jednoosobowego podpisania umowy leasingowej i weksli in blanco.`);
                  setSpecificClausesText("Uchwała podjęta jednogłośnie w głosowaniu jawnym (100 głosów 'za', 0 'przeciw', 0 'wstrzymujących się'). Uchwała wchodzi w życie z dniem podjęcia.");
                  setError(null);
                }}
                className="p-2.5 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-indigo-500/50 rounded-lg text-left transition-all cursor-pointer group"
              >
                <div className="text-xs font-bold text-indigo-300 group-hover:text-indigo-200 flex items-center gap-1.5">
                  <span>🇵🇱 Uchwała Wspólników Sp. z o.o. (KSH)</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">Kodeks spółek handlowych • Art. 230 KSH</div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 flex items-center justify-between">
                  <span>{isPl ? "Typ dokumentu prawnego" : isUk ? "Тип юридичного документа" : "Legal Document Type"}</span>
                  <span className="text-[10px] font-mono text-purple-600 font-normal">🇵🇱 Polska • 🇺🇦 Україна • 🇪🇺 UE</span>
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:outline-none text-sm transition-colors text-slate-900 font-semibold"
                >
                  <optgroup label="🇵🇱 Prawo Pracy & Umowy Cywilnoprawne (Кадри, Праця & ЦПД Польщі)">
                    <option value="Umowa o pracę (Kodeks pracy / Polska)">🇵🇱 Umowa o pracę (Kodeks pracy — okres próbny / czas określony / nieokreślony)</option>
                    <option value="Umowa zlecenie (Kodeks cywilny / ZUS)">🇵🇱 Umowa zlecenie (Kodeks cywilny — stawka godzinowa, ewidencja, ZUS)</option>
                    <option value="Umowa o dzieło (Kodeks cywilny / Prawa autorskie)">🇵🇱 Umowa o dzieło (Prawa autorskie 50% KUP / rezultat)</option>
                    <option value="Umowa o zachowaniu poufności (NDA / Polska)">🇵🇱 Umowa o zachowaniu poufności (NDA — tajemnica przedsiębiorstwa)</option>
                    <option value="Umowa o zakazie konkurencji (Kodeks pracy / B2B)">🇵🇱 Umowa o zakazie konkurencji (w trakcie i po ustaniu stosunku prawnego)</option>
                  </optgroup>
                  <optgroup label="🇵🇱 Budownictwo, Nieruchomości & Usługi (Будівництво, Нерухомість & B2B)">
                    <option value="Umowa o świadczenie usług B2B (Kodeks cywilny / KSeF)">🇵🇱 Umowa o świadczenie usług B2B (Kodeks cywilny / KSeF / Reverse Charge)</option>
                    <option value="Protokół odbioru robót budowlanych (Prawo budowlane)">🇵🇱 Protokół odbioru robót budowlanych (Kierownik budowy / Inspektor nadzoru)</option>
                    <option value="Umowa o roboty budowlane (Kodeks cywilny / KNR)">🇵🇱 Umowa o roboty budowlane / Generalne wykonawstwo (Kaucja gwarancyjna)</option>
                    <option value="Umowa najmu lokalu użytkowego (Polska / GUS)">🇵🇱 Umowa najmu lokalu użytkowego (Czynsz EUR/PLN, kaucja, waloryzacja GUS)</option>
                    <option value="Umowa sprzedaży towarów B2B / Dostawa (KSeF)">🇵🇱 Umowa sprzedaży towarów B2B / Dostawa (Incoterms 2020, KSeF)</option>
                  </optgroup>
                  <optgroup label="🇵🇱 Spółki Handlowe & Korporacyjne (Корпоративні документи Sp. z o.o.)">
                    <option value="Uchwała Zarządu / Wspólników Sp. z o.o. (KSH)">🇵🇱 Uchwała Zarządu / Zgromadzenia Wspólników Sp. z o.o. (KSH)</option>
                    <option value="Pełnomocnictwo ogólne / handlowe (Polska / KAS / ZUS)">🇵🇱 Pełnomocnictwo ogólne / handlowe (Reprezentacja przed US, ZUS, KAS)</option>
                    <option value="Protokół Zwyczajnego Zgromadzenia Wspólników (Zatwierdzenie sprawozdania)">🇵🇱 Protokół ZWZ Wspólników Sp. z o.o. (Zatwierdzenie bilansu i sprawozdania)</option>
                  </optgroup>
                  <optgroup label="🇪🇺 Międzynarodowe Kontrakty Dwujęzyczne (Двомовні Міжнародні Контракти)">
                    <option value="Двомовний договір UA-PL (Паралельні статті)">🇪🇺 Двомовний контракт UA-PL (Польсько-Український, паралельні колонки)</option>
                    <option value="Двомовний договір UA-EN (Паралельні статті)">🇪🇺 Двомовний контракт UA-EN (Англо-Український, паралельні колонки)</option>
                    <option value="Договір підряду за стандартами FIDIC (Red/Yellow Book)">🇪🇺 Контракт за стандартами FIDIC (Red/Yellow Book Subcontract)</option>
                    <option value="Довідка кваліфікаційної відповідності закупівлі TED / PZP">🇪🇺 Довідка закупівлі за нормами TED / PZP (Європейські тендери)</option>
                  </optgroup>
                  <optgroup label="🇺🇦 Юрисдикція України (ДСТУ / ЦКУ / ПКУ)">
                    <option value="Позовна заява">{isUk ? "Позовна заява (Господарський / Цивільний суд)" : "Lawsuit Statement (Ukraine)"}</option>
                    <option value="Претензія">{isUk ? "Претензія про сплату заборгованості" : "Pre-litigation Claim (Ukraine)"}</option>
                    <option value="Скарга">{isUk ? "Скарга до регулятора / АМКУ / ДПС" : "Complaint (Ukraine)"}</option>
                    <option value="Договір поставки">{isUk ? "Договір поставки / купівлі-продажу" : "Supply Agreement (Ukraine)"}</option>
                    <option value="Договір оренди">{isUk ? "Договір оренди комерційної нерухомості" : "Lease Agreement (Ukraine)"}</option>
                    <option value="Договір про надання послуг">{isUk ? "Договір про надання послуг / підряду" : "Services Agreement (Ukraine)"}</option>
                  </optgroup>
                  <option value="Інший юридичний документ">{isPl ? "🇵🇱 Inny indywidualny dokument prawny" : isUk ? "Інший індивідуальний юридичний документ" : "Custom Legal Document"}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  {isPl ? "Stan faktyczny / Dane stron i warunki dokumentu" : isUk ? "Обставини справи / Вимоги та реквізити сторін" : "Facts of the case / Document requirements"}
                </label>
                <textarea
                  placeholder={isPl
                    ? "Opisz dane stron (NIP, KRS, REGON, adresy), przedmiot umowy, wynagrodzenie, terminy, obowiązki stron i zakres odpowiedzialności..."
                    : isUk 
                    ? "Опишіть обставини (хто, кому, коли, предмет договору/позову, реквізити сторін, сума, терміни виконання зобов'язань тощо)..." 
                    : "Describe the facts (parties, NIP/EDRPOU, contract subject, price, deliverables, timelines, liability terms)..."}
                  value={caseFactsText}
                  onChange={(e) => setCaseFactsText(e.target.value)}
                  rows={8}
                  className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 text-xs sm:text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  {isPl ? "Klauzule szczególne / Warunki dodatkowe (opcjonalnie)" : isUk ? "Особливі побажання / Додаткові застереження (необов'язково)" : "Special requests / Additional clauses (optional)"}
                </label>
                <textarea
                  placeholder={isPl
                    ? "Wskaż klauzule szczególne: kary umowne (w PLN/EUR), właściwość sądu (np. Sąd Arbitrażowy KIG), poufność NDA, KSeF, fakturowanie, gwarancja..."
                    : isUk 
                    ? "Вкажіть особливі пункти (наприклад: пеня, штрафи, підсудність арбітражу, KSeF, Reverse Charge VAT, форс-мажор, гарантія якості)..." 
                    : "List specific clauses (penalties in PLN/EUR, arbitration court, NDA, KSeF invoicing, warranties, force majeure)..."}
                  value={specificClausesText}
                  onChange={(e) => setSpecificClausesText(e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-300 rounded-xl focus:border-purple-500 focus:ring-1 focus:ring-purple-500/30 text-xs sm:text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!caseFactsText.trim()}
              className="w-full py-3.5 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 hover:from-purple-500 hover:to-indigo-500 disabled:bg-slate-200 disabled:text-slate-400 text-white font-extrabold text-xs sm:text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-all cursor-pointer shadow-lg shadow-purple-600/30 hover:scale-[1.01]"
            >
              <FileSignature className="w-5 h-5 text-amber-300" />
              <span>{isPl ? "🚀 Wygeneruj Dokument Prawny" : isUk ? "🚀 Сформувати Юридичний Документ" : "🚀 Generate Legal Document"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-purple-600" />
                <span>{isPl ? "Standardy i Zgodność Prawna" : isUk ? "Правовий комплаєнс & Нормативи" : "Legal Standards & Compliance"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-700 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-700 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isPl ? "100% zgodność z Kodeksem cywilnym, Kodeksem pracy, KSH oraz wymogami KSeF w Polsce." : isUk ? "Повна відповідність ЦКУ, нормам Польщі (Kodeks cywilny, KSH) та нормам KSeF." : "Full statutory compliance with Civil Code, Labor Code, Commercial Companies Code (KSH), and KSeF."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-700 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isPl ? "Generowanie gotowego pliku Word (.docx) z pełną strukturą artykułów i podpisami stron." : isUk ? "Формування нативного Word (.docx) документа зі структурованими статтями та реквізитами сторін." : "Generates clean Word (.docx) ready for signing and execution."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-purple-100 text-purple-700 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isPl ? "Opcja natychmiastowego podpisu cyfrowego (QES / KEP) z pobraniem potwierdzenia audytu." : isUk ? "Можливість накладання КЕП / Дія.Підпис та експорту в Telegram." : "Direct digital signature integration (QES / KEP) with audit trail receipt."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-purple-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-purple-500 rounded-full animate-spin" />
            <FileSignature className="w-6 h-6 text-purple-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Складання документа..." : "Generating Document by AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Audit Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-550 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-purple-50 p-1.5 rounded border border-purple-100">
                <FileText className="w-4 h-4 text-purple-650" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Створений документ" : "Generated Document"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <TelegramExportButton
                title={documentType || 'Юридичний документ'}
                content={report}
                agentName="Конструктор юридичних документів"
                agentId="legal-doc-creator"
                isUk={isUk}
                variant="compact"
              />

              <ExcelExportButton
                title={documentType || 'Розрахунок до договору'}
                content={report}
                fileName={`legal_doc_${Date.now()}`}
                isUk={isUk}
                variant="compact"
              />

              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>

              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>

              <button 
                onClick={() => setShowSignModal(true)}
                className="py-1.5 px-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
                title="Накласти КЕП / Дія.Підпис"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-white" />
                <span>{isUk ? '✍️ Підписати КЕП' : '✍️ Sign E-QES'}</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setCaseFactsText('');
                setSpecificClausesText('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Створити новий документ" : "Create New Document"}
            </button>
          </div>
        </div>
      )}

      {/* Digital Signature Modal Integration */}
      <DigitalSignatureModal 
        isOpen={showSignModal}
        onClose={() => setShowSignModal(false)}
        documentTitle={documentType || 'Юридичний документ'}
        documentContent={report || ''}
      />
    </div>
  );
}
